import React, { useEffect, useMemo, useRef, useState } from 'react';
import CardCommon from '@op/components/CardCommon';
import useLanguage from '@op/hooks/useLanguage';
import { Collapse, Tag } from 'tea-component';
import AiImg from '@op/assets/svg/icon-ai.svg';
import { getAITagColorKey } from '../../utils';
import './index.scss';
import classnames from 'classnames';
import TextBubbles from '@home/components/TextBubbles';
import ArrowIcon from '@op/assets/svg/arrow-icon.svg';

interface ChildTag {
  label_en: string;
  label_zh: string;
}

interface TagGroup {
  label_en: string;
  label_zh: string;
  children: ChildTag[];
}

interface ITagCardProps {
  list: TagGroup[];
  className?: string;
  [key: string]: any;
}

interface AITagColor {
  color: string;
  backgroundColor: string;
}

export const AI_TAGS_COLORS: AITagColor[] = [
  { color: "#8E51DA", backgroundColor: "#EDE3F9" },
  { color: "#1FA6AA", backgroundColor: "#DBF1F1" },
  { color: "#B01BB6", backgroundColor: "#F2DAF3" },
  { color: "#F756A9", backgroundColor: "#FDE4F1" },
  { color: "#FF7D00", backgroundColor: "#FFEAD6" },
  { color: "#1DB440", backgroundColor: "#DBF3E0" },
  { color: "#E9B32B", backgroundColor: "#FBF3DD" },
  { color: "#F6685D", backgroundColor: "#FDE7E5" },
  { color: "#029CD4", backgroundColor: "#E4DBF2" },
  { color: "#551DB0", backgroundColor: "#F2D9F3" },
  { color: "#B010B6", backgroundColor: "#FFEAD6" },
  { color: "#0E42D2", backgroundColor: "#D8E1F8" },
  { color: "#57A9FB", backgroundColor: "#E4F1FE" },
  { color: "#A23511", backgroundColor: "#F0DED9" },
  { color: "#008026", backgroundColor: "#D6EADC" },
  { color: "#774B04", backgroundColor: "#E9E2D7" },
  { color: "#A1151E", backgroundColor: "#F0D9DB" },
  { color: "#77064F", backgroundColor: "#E9D7E3" }
];

export default function TagCard(props: ITagCardProps) {
  const { isEn, formatMessage } = useLanguage();
  const { list, className, ...restCardProps } = props;
  const [activeIds, setActiveIds] = useState<string[]>([]);
  const allIds = useMemo(() => list.map((_, idx) => String(idx)), [list]);
  const allCollapsed = activeIds.length === 0;
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setActiveIds(allIds);
  }, [allIds]);

  const handleToggleAll = () => {
    setActiveIds(allCollapsed ? allIds : []);
  };

  useEffect(() => {
    const target = document.querySelector('.dbo-feeds-reviews-card') as HTMLElement;
    const emptyTarget = document.querySelector('.dbo-no-data') as HTMLElement;
    if (!target && !emptyTarget) return;
    const setHeight = () => {

      if (cardRef.current && target) {
        cardRef.current.style.height = `${target.offsetHeight - 70}px`;
      }

      if (emptyTarget && target) {
        emptyTarget.style.height = `${target.offsetHeight - 70}px`;
      }
    };
    if (!restCardProps?.loading) {
      setHeight();
    }
    window.addEventListener('resize', setHeight);
    return () => {
      window.removeEventListener('resize', setHeight);
    };
  }, [list, restCardProps]);

  const localColor = useMemo(() => {
    let localColor = null;
    try {
      const raw = sessionStorage.getItem(getAITagColorKey());
      localColor = raw ? JSON.parse(raw) : null;
    } catch (err) {
      console.warn("Failed to load AI_TAG_COLOR from localStorage:", err);
      localColor = null;
    }

    return localColor;
  }, []);

  return (
    <CardCommon
      title={
        <div className="dbo-video-tag-card-title">
          <img src={AiImg} className="dbo-inlineflex-img" />
          {formatMessage({ id: "AI Tags" })}
        </div>
      }
      dropdownsFilterBeforeElement={
        <span className="dbo-video-tag-card-toggle" onClick={handleToggleAll}>
          {formatMessage({ id: allCollapsed ? "Expand All" : "Collapse All" })}
        </span>
      }
      className={classnames("dbo-video-tag-card", className)}
      empty={list?.length === 0}
      {...restCardProps}
    >
      <div className="dbo-video-tag-card-content" ref={cardRef}>
        {list.map((group, index) => {
          const id = String(index);
          const colorSet = localColor?.[group.label_en] || AI_TAGS_COLORS[index];

          return (
            <Collapse
              key={id}
              activeIds={activeIds}
              onActive={(ids) => setActiveIds(ids)}
              icon={(active) => {
                return (
                  <img
                    style={{
                      transform: !active ? "rotate(-90deg)" : "rotate(0deg)",
                      transition: "transform 0.2s ease",
                    }}
                    src={ArrowIcon}
                    className='dbo-video-tag-card-arrow-icon'
                  />
                );
              }}
            >
              <Collapse.Panel
                id={id}
                title={isEn ? group.label_en : group.label_zh}
              >
                <div className="flex flex-wrap gap-2 mt-2">
                  {group.children.map((tag, i) => (
                    <Tag
                      key={i}
                      className="dbo-video-tag-card-tag"
                      style={{
                        backgroundColor: colorSet.backgroundColor,
                        color: colorSet.color,
                      }}
                    >
                      <TextBubbles
                        className='dbo-video-tag-card-tag-bubble'
                        rowNum={1}
                        text={isEn ? tag.label_en : tag.label_zh}
                        placement={'left'}
                      />
                    </Tag>
                  ))}
                </div>
              </Collapse.Panel>
            </Collapse>
          );

        })}
      </div>
    </CardCommon>
  );
}
